//The purpose of creating a separate file is to keep ownership of the file with team 3b till RnP feature is not complete.
import _ from "lodash";
import ACLStorage from "lib/aclService";
import ACLStore from "lib/aclStore";

import {
  parseAdminMenuId,
  getModuleNameFromUid,
  checkForAcademicYearRestriction,
} from "./Utils";
import {
  ADMIN_PORTAL_ACCESS_PERMISSION,
  ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION,
  ENABLE_RNP_CONSUMPTION_IN_BEHAVIOUR,
  ENABLE_PROGRAM_ROLES_FOR_INTEGRATIONS,
  IS_TRANSCRIPT_DISABLED_FOR_ORG,
} from "Constants/permissionConstants";
import { isMessagingV2Enabled } from "MessagingV2/modules/Utils";
import { getUserCurriculumPrograms } from "modules/CommonSelectors";
import { isAdminTimeTableEnabled } from "Administrator/routes/Timetable/modules/Utils";
import { getIsAdminByRole } from "lib/staffPermissionUtils";
import { getIsLearningCoursesEnabled } from "LearningCourses/modules/Utils";
import {
  getIsBehaviourSetupCardEnabled,
  getHasModuleAdminAccess as getBehaviourAdminAccess,
} from "BehaviourManagement/modules/Utils";
import { getIsIBISUIRevampEnabled } from "Utils/ACLStoreUtils";
import {
  checkIsSentralFeatureEnabledInCurriculum,
  checkIsSentralFeatureEnabledInCurriculums,
  SENTRAL_PERMISSION_CONFIGURATIONS,
} from "Login/components/SentralPortalModal/SentralPortalUtils";

// Memoization is used to maintain the same reference of the object for the same input.
export const getVariablesForAdminPortalHomePagePermissions = ({
  curriculumPrograms,
}) => {
  const contexts = [[]];

  const programsCount = _.size(curriculumPrograms);

  for (let i = 0; i < programsCount; i++) {
    const { id } = curriculumPrograms[i];

    const context = [
      {
        entityId: id,
        entityType: "CURRICULUM_PROGRAM",
      },
    ];

    contexts.push(context);
  }

  return {
    filters: {
      module: "ADMIN_PORTAL",
      appType: ["ADMIN_WEB"],
      contexts,
    },
  };
};

export const getRnPPermissions = ({ item }) => {
  const permissions = [];

  /**
   * If node has own permission then consider it as well.
   */
  const itemPermission = _.get(item, "permission");
  if (itemPermission) {
    permissions.push(itemPermission);
  }

  return permissions;
};

const _getNodeVisibilityStatusWrtToRnP = ({ permissions, uid }) => {
  /**
   * Hack: Till all cards in Admin Menu do not use Role based permissions,
   * we will return false if permissions are empty, which is the case for cards
   * using existing configurations.
   */
  if (_.isEmpty(permissions)) {
    return false;
  }

  /**
   * Here, if curriculumProgramId is present in uid, then it means that the node is
   * shown program wise(Ex: SnP, Unit Plans etc.)
   */
  const { curriculumProgramId } = parseAdminMenuId(uid);

  /**
   * If curriculumProgramId is not present, then it means that the node is not dependent
   * on any program(Ex: School Setup, Messaging etc.)So, we will check value
   * in context of the organization.
   */
  if (!curriculumProgramId) {
    return _.some(permissions, permission => {
      return ACLStorage.getModuleSettingsValue(permission);
    });
  }

  /**
   * If curriculumProgramId is present, then it means that the node is shown program wise(Ex: SnP, Unit Plans etc.).
   * So, while evaluating permission for the node, we need to consider program as context and need to get value
   * in program context.
   */
  return _.some(permissions, permission => {
    return ACLStorage.getModuleSettingsValueByContext(permission, [
      { entityType: "CURRICULUM_PROGRAM", entityId: curriculumProgramId },
    ]);
  });
};

/**
 * This utility function will check whether node can appear in the UI or not.
 * It will check for the following:
 * - Whether some of the RnP permissions are enabled or not.
 * - Whether node should appear based on the current academic year selection.
 */
export const checkNodeVisibility = ({
  permissions,
  uid,
  rootNode,
  isCurrentAcademicYearSelected,
  isFutureAcademicYearSelected,
}) => {
  const isRolesAndPermissionAccessAllowed = _getNodeVisibilityStatusWrtToRnP({
    permissions,
    uid,
  });

  const moduleName = getModuleNameFromUid(uid);

  // Checking for module access outside of current academic year
  const accessibleOutsideCurrentAcademicYear = !isCurrentAcademicYearSelected
    ? rootNode ||
      !checkForAcademicYearRestriction({
        moduleName,
        isFutureAcademicYearSelected,
      })
    : true;

  return (
    accessibleOutsideCurrentAcademicYear && isRolesAndPermissionAccessAllowed
  );
};

export const checkIfAnyCardIsVisibleInAdminPortal = ({
  nodes,
  isCurrentAcademicYearSelected,
  isFutureAcademicYearSelected,
  curriculumProgramIds,
  rootNodes,
}) => {
  return _.some(rootNodes, rootNode => {
    const { uid } = rootNode;
    return getAdminPortalNodeVisibilityStatus({
      item: rootNode,
      nodes,
      rootNode: true,
      isCurrentAcademicYearSelected,
      isFutureAcademicYearSelected,
      isCurriculumConfigurationsRootNode: uid === "CURRICULUM_CONFIGURATIONS",
      curriculumProgramIds,
    });
  });
};

export const curriculumIdsForWhichNodeIsVisible = ({
  permission,
  curriculumProgramIds,
}) => {
  return _.filter(curriculumProgramIds, curriculumProgramId => {
    return ACLStorage.getModuleSettingsValueByContext(permission, [
      { entityType: "CURRICULUM_PROGRAM", entityId: curriculumProgramId },
    ]);
  });
};

const getRnPPermissionsForSchoolSetup = ({ item, nodes }) => {
  const children = _.get(item, "children", []);
  const permissions = {
    accountLevelPermissions: [],
    programLevelPermissions: [],
  };

  const isRnPEnabled = ACLStore.can(ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION);

  const collectPermissions = nodeItem => {
    if (!nodeItem) return;

    //Required for integration node because when we read its mapping it always return true.
    let skipForTeacher = nodeItem.skipForTeacher;
    const isAdmin = getIsAdminByRole();

    // No need to skip node if user is admin.
    skipForTeacher = !isAdmin && skipForTeacher;

    if (isRnPEnabled) {
      skipForTeacher = false;
    }

    if (skipForTeacher) return;

    let hasPermissions = false;

    if (nodeItem.permission) {
      permissions.accountLevelPermissions.push(nodeItem.permission);
      hasPermissions = true;
    }

    if (nodeItem.accountLevelPermission) {
      permissions.accountLevelPermissions.push(nodeItem.accountLevelPermission);
      hasPermissions = true;
    }

    if (nodeItem.programLevelPermission) {
      permissions.programLevelPermissions.push(nodeItem.programLevelPermission);
      hasPermissions = true;
    }

    // If no permissions found, check its children recursively
    if (!hasPermissions && nodeItem.children) {
      nodeItem.children.forEach(childId => {
        const childNode = _.find(nodes, { uid: childId });
        collectPermissions(childNode);
      });
    }
  };

  children.forEach(childId => {
    const nodeItem = _.find(nodes, { uid: childId });
    collectPermissions(nodeItem);
  });

  return permissions;
};

const getRnPPermissionsForSchoolRoster = ({ item }) => {
  return {
    accountLevelPermissions: item.accountLevelPermission
      ? [item.accountLevelPermission]
      : [],
    programLevelPermissions: item.programLevelPermission
      ? [item.programLevelPermission]
      : [],
  };
};

export const checkSchoolRosterIndividualLevelVisibilityWithCurriculumPrograms = ({
  item,
  curriculumProgramIds,
}) => {
  const {
    accountLevelPermissions,
    programLevelPermissions,
  } = getRnPPermissionsForSchoolRoster({
    item,
  });

  const isVisibleOnAccountLevel = _.some(accountLevelPermissions, permission =>
    ACLStorage.getModuleSettingsValueByContext(permission, [])
  );

  if (isVisibleOnAccountLevel) {
    return {
      accountLevelVisibility: true,
      curriculumProgramIds,
    };
  }

  const userCurriculumPrograms = getUserCurriculumPrograms();

  const validCurriculumProgramIds = _.reduce(
    programLevelPermissions,
    (res, permission) => {
      const curriculumIds = curriculumIdsForWhichNodeIsVisible({
        permission,
        curriculumProgramIds: _.map(userCurriculumPrograms, "id"),
      });

      return [...res, ...curriculumIds];
    },
    []
  );

  return {
    accountLevelVisibility: false,
    programLevelVisibility: !_.isEmpty(validCurriculumProgramIds),
    curriculumProgramIds: _.uniq(validCurriculumProgramIds),
  };
};

export const checkIntegrationsIndividualLevelVisibilityWithCurriculumPrograms = ({
  item,
  curriculumProgramIds,
}) => {
  const {
    accountLevelPermissions,
    programLevelPermissions,
  } = getRnPPermissionsForSchoolRoster({
    item,
  });

  const isVisibleOnAccountLevel = _.some(accountLevelPermissions, permission =>
    ACLStorage.getModuleSettingsValueByContext(permission, [])
  );

  if (isVisibleOnAccountLevel) {
    return {
      accountLevelVisibility: true,
      curriculumProgramIds,
    };
  }

  const userCurriculumPrograms = getUserCurriculumPrograms();

  const validCurriculumProgramIds = _.reduce(
    programLevelPermissions,
    (res, permission) => {
      const curriculumIds = curriculumIdsForWhichNodeIsVisible({
        permission,
        curriculumProgramIds: _.map(userCurriculumPrograms, "id"),
      });

      return [...res, ...curriculumIds];
    },
    []
  );
  return {
    accountLevelVisibility: false,
    programLevelVisibility: !_.isEmpty(validCurriculumProgramIds),
    curriculumProgramIds: _.uniq(validCurriculumProgramIds),
  };
};

export const getAdminPortalNodeVisibilityStatus = ({
  item,
  nodes,
  rootNode,
  isCurrentAcademicYearSelected,
  isFutureAcademicYearSelected,
  isCurriculumConfigurationsRootNode,
  curriculumProgramId,
  curriculumProgramIds,
  curriculumConfigMap,
}) => {
  const itemUid = _.get(item, "uid");

  switch (true) {
    case itemUid === "LEARNING_COURSE_SETUP": {
      const programIds = curriculumProgramId
        ? [curriculumProgramId]
        : curriculumProgramIds;

      if (_.size(programIds) === 0) {
        return false;
      }

      const isLearningCourseEnabled = _.some(programIds, programId => {
        return getIsLearningCoursesEnabled({
          curriculumProgramId: programId,
        });
      });

      return isLearningCourseEnabled;
    }
    /**
     * For children nodes of LEARNING_COURSE_SETUP
     * Ex: 123:PYP_LEARNING_COURSE_SETUP, etc.
     * Below logic is mainly for access blocker, if user hits the url directly we need to check permission for that particular program.
     */
    case /.+(LEARNING_COURSE_SETUP)$/.test(itemUid): {
      const curriculumProgramId = itemUid.split(":")?.[0];
      return getIsLearningCoursesEnabled({
        curriculumProgramId,
      });
    }
    case itemUid === "SCHOOL_CONFIGURATIONS":
    case itemUid === "CURRICULUM_CONFIGURATIONS":
    case item.isProgramRootNode:
    case itemUid === "EXTERNAL_STAFF": {
      const children = _.get(item, "children", []);
      return _.some(children, childId => {
        const nodeItem = _.find(nodes, { uid: childId });

        if (nodeItem) {
          return getAdminPortalNodeVisibilityStatus({
            item: nodeItem,
            nodes,
            rootNode,
            isCurrentAcademicYearSelected,
            isFutureAcademicYearSelected,
            isCurriculumConfigurationsRootNode,
            curriculumProgramIds,
          });
        }

        return false;
      });
    }

    case itemUid === "MESSAGING_V2":
      return isMessagingV2Enabled();
    case itemUid === "SCHOOL_SETUP": {
      const {
        accountLevelPermissions,
        programLevelPermissions,
      } = getRnPPermissionsForSchoolSetup({
        item,
        nodes,
      });

      const isVisibleOnAccountLevel = _.some(
        accountLevelPermissions,
        permission => ACLStorage.getModuleSettingsValueByContext(permission, [])
      );

      const isVisibleOnProgramLevel = _.some(
        programLevelPermissions,
        permission =>
          _.some(curriculumProgramIds, curriculumProgramId =>
            ACLStorage.getModuleSettingsValueByContext(permission, [
              {
                entityType: "CURRICULUM_PROGRAM",
                entityId: curriculumProgramId,
              },
            ])
          )
      );

      return isVisibleOnAccountLevel || isVisibleOnProgramLevel;
    }
    case itemUid === "CLASS_ROSTER": {
      const {
        accountLevelVisibility,
        programLevelVisibility,
      } = checkSchoolRosterIndividualLevelVisibilityWithCurriculumPrograms({
        item,
        curriculumProgramIds,
      });

      return accountLevelVisibility || programLevelVisibility;
    }
    /**
     * For children nodes of CLASS_ROSTER
     */
    case itemUid === "COURSE_SETUP":
    case itemUid === "TEACHER_SETUP":
    case itemUid === "STUDENTS":
    case itemUid === "FAMILY_MEMBERS": {
      const parentNode = _.find(nodes, { uid: "CLASS_ROSTER" });
      const {
        accountLevelVisibility,
        programLevelVisibility,
      } = checkSchoolRosterIndividualLevelVisibilityWithCurriculumPrograms({
        item: parentNode,
        curriculumProgramIds,
      });

      return accountLevelVisibility || programLevelVisibility;
    }
    /**
     * For children nodes of COURSE_SETUP, TEACHER_SETUP, STUDENTS and FAMILY_MEMBERS,
     * Ex: 123:PYP_COURSE_SETUP, ALL_TEACHER_SETUP, 123:PYP_TEACHER_SETUP, 123:PYP_STUDENTS etc.
     * Below logic is mainly for access blocker, if user hits the url directly we need to check permission for that particular program.
     */
    case /.+(COURSE_SETUP|TEACHER_SETUP|STUDENTS|FAMILY_MEMBERS)$/.test(
      itemUid
    ): {
      const parentNode = _.find(nodes, { uid: "CLASS_ROSTER" });
      const {
        accountLevelVisibility,
        curriculumProgramIds: validCurriculumProgramIds,
      } = checkSchoolRosterIndividualLevelVisibilityWithCurriculumPrograms({
        item: parentNode,
        curriculumProgramIds,
      });

      /**
       * Need to check feature flags also for children nodes along with RnP permissions.
       * Need to add aclstore check for dev control features. Ex: having rnp access for a module but it's own feature flag is off.
       */
      if (_.startsWith(itemUid, "ALL")) {
        return accountLevelVisibility && ACLStore.can(item.perm);
      }

      const { curriculumProgramId } = parseAdminMenuId(itemUid);
      return (
        (accountLevelVisibility ||
          _.includes(validCurriculumProgramIds, curriculumProgramId)) &&
        ACLStore.can(item.perm)
      );
    }
    /**  For children nodes of INTEGRATIONS */
    case itemUid === "SYNCV2":
    case itemUid === "ATTENDANCE_SYNC":
    case itemUid === "TIMETABLE_SYNC":
    case itemUid === "GRADEBOOK_SYNC":
    case itemUid === "ASSESSMENT_SYNC": {
      const parentNode = _.find(nodes, { uid: "INTEGRATIONS" });

      const {
        accountLevelVisibility,
        programLevelVisibility,
      } = checkIntegrationsIndividualLevelVisibilityWithCurriculumPrograms({
        item: parentNode,
        curriculumProgramIds,
      });

      if (ACLStore.can(item.perm)) {
        return accountLevelVisibility || programLevelVisibility;
      }
      return false;
    }
    // no program level permissions for this only on permission & account
    case itemUid === "TURNITIN_SETTINGS":
    case itemUid === "LTI_SETTINGS":
    case itemUid === "SYNC":
    case itemUid === "INTEGRATIONS_INTERNAL_TOOL":
    case itemUid === "INTEGRATION_ANALYTICS": {
      const parentNode = _.find(nodes, { uid: "INTEGRATIONS" });

      const {
        accountLevelVisibility,
      } = checkIntegrationsIndividualLevelVisibilityWithCurriculumPrograms({
        item: parentNode,
        curriculumProgramIds,
      });

      return accountLevelVisibility && ACLStore.can(item.perm);
    }

    case itemUid.endsWith("GRADES_SETUP"):
    case itemUid.endsWith("PERIODS_SETUP"): {
      const parentNode = _.find(nodes, { uid: "SCHOOL_SETTINGS" });
      const permissions = getRnPPermissions({
        item: parentNode,
        nodes,
      });

      return (
        checkNodeVisibility({
          permissions,
          uid: parentNode.uid,
          rootNode,
          isCurrentAcademicYearSelected,
          isFutureAcademicYearSelected,
        }) && ACLStore.can(item.perm)
      );
    }
    case itemUid === "SCHOOL_SETTINGS_MODULE": {
      if (ACLStore.can(ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION)) {
        const permissions = getRnPPermissions({
          item,
          nodes,
        });

        return checkNodeVisibility({
          permissions,
          uid: item.uid,
          rootNode,
          isCurrentAcademicYearSelected,
          isFutureAcademicYearSelected,
        });
      } else {
        const children = _.get(item, "children", []);
        return _.some(children, childId => {
          const nodeItem = _.find(nodes, { uid: childId });

          if (nodeItem) {
            return ACLStore.can(nodeItem.perm);
          }

          return false;
        });
      }
    }
    case itemUid === "INTEGRATIONS": {
      if (ACLStore.can(ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION)) {
        const enableProgramRolesForIntegrations = ACLStore.can(
          ENABLE_PROGRAM_ROLES_FOR_INTEGRATIONS
        );

        if (enableProgramRolesForIntegrations) {
          const {
            accountLevelVisibility,
            programLevelVisibility,
          } = checkIntegrationsIndividualLevelVisibilityWithCurriculumPrograms({
            item,
            curriculumProgramIds,
          });

          return accountLevelVisibility || programLevelVisibility;
        } else {
          const permissions = getRnPPermissions({
            item,
            nodes,
          });

          return checkNodeVisibility({
            permissions,
            uid: item.uid,
            rootNode,
            isCurrentAcademicYearSelected,
            isFutureAcademicYearSelected,
          });
        }
      } else {
        const children = _.get(item, "children", []);
        return _.some(children, childId => {
          const nodeItem = _.find(nodes, { uid: childId });

          if (nodeItem) {
            return ACLStore.can(nodeItem.perm);
          }

          return false;
        });
      }
    }
    case itemUid === "USER_NAME_SETTINGS_V2":
    case itemUid === "SCHOOL_MODULE_SETTINGS":
    case itemUid === "MODULE_SETTINGS_V2":
    case itemUid === "STUDENT_ALERT_SETTINGS": {
      const parentNode = _.find(nodes, { uid: "SCHOOL_SETTINGS_MODULE" });
      if (!parentNode) {
        return false;
      }

      let isNodeVisible = false;
      if (item?.permission) {
        isNodeVisible = ACLStorage.getModuleSettingsValue(item.permission);
      } else {
        isNodeVisible = ACLStore.can(item.perm);
      }

      return _.includes(parentNode.children, itemUid) && isNodeVisible;
    }
    case itemUid === "ATTENDANCE_SETTINGS": {
      if (ACLStore.can(ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION)) {
        const permissions = getRnPPermissions({
          item,
          nodes,
        });

        return checkNodeVisibility({
          permissions,
          uid: item.uid,
          rootNode,
          isCurrentAcademicYearSelected,
          isFutureAcademicYearSelected,
        });
      } else {
        return ACLStore.can(item.perm);
      }
    }

    case itemUid === "ATTENDANCE_SETTINGS_V2": {
      if (ACLStore.can(ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION)) {
        const isNodeVisible = _.some(
          curriculumProgramIds,
          curriculumProgramId => {
            return ACLStorage.getModuleSettingsValueByContext(
              _.get(item, "permission"),
              [
                {
                  entityType: "CURRICULUM_PROGRAM",
                  entityId: curriculumProgramId,
                },
              ],
              { addOnContexts: [] }
            );
          }
        );

        return isNodeVisible;
      } else {
        return ACLStore.can(item.perm);
      }
    }

    case _.includes(itemUid, "PYP_ATTENDANCE_AND_TIMETABLE_SETTINGS"):
    case _.includes(itemUid, "MYP_ATTENDANCE_AND_TIMETABLE_SETTINGS"):
    case _.includes(itemUid, "DP_ATTENDANCE_AND_TIMETABLE_SETTINGS"):
    case _.includes(itemUid, "UBD_ATTENDANCE_AND_TIMETABLE_SETTINGS"): {
      const { curriculumProgramId } = parseAdminMenuId(itemUid);

      const isSentralFeatureEnabled = checkIsSentralFeatureEnabledInCurriculum(
        SENTRAL_PERMISSION_CONFIGURATIONS.TIMETABLE,
        curriculumConfigMap,
        curriculumProgramId
      );

      if (isSentralFeatureEnabled) return false;
      const permissions = getRnPPermissions({
        item,
        nodes,
      });
      return checkNodeVisibility({
        permissions,
        uid: item.uid,
        rootNode,
        isCurrentAcademicYearSelected,
        isFutureAcademicYearSelected,
      });
    }

    case _.includes(itemUid, "PYP_TIMETABLE"):
    case _.includes(itemUid, "MYP_TIMETABLE"):
    case _.includes(itemUid, "DP_TIMETABLE"):
    case _.includes(itemUid, "UBD_TIMETABLE"): {
      const { curriculumProgramId } = parseAdminMenuId(itemUid);

      const isSentralFeatureEnabled = checkIsSentralFeatureEnabledInCurriculum(
        SENTRAL_PERMISSION_CONFIGURATIONS.TIMETABLE,
        curriculumConfigMap,
        curriculumProgramId
      );

      if (isSentralFeatureEnabled) return false;

      if (isAdminTimeTableEnabled()) {
        const permissions = getRnPPermissions({
          item,
          nodes,
        });
        return checkNodeVisibility({
          permissions,
          uid: item.uid,
          rootNode,
          isCurrentAcademicYearSelected,
          isFutureAcademicYearSelected,
        });
      }
      return false;
    }

    case _.includes(itemUid, "PYP_ATTENDANCE_DASHBOARD"):
    case _.includes(itemUid, "MYP_ATTENDANCE_DASHBOARD"):
    case _.includes(itemUid, "DP_ATTENDANCE_DASHBOARD"):
    case _.includes(itemUid, "UBD_ATTENDANCE_DASHBOARD"): {
      const { curriculumProgramId } = parseAdminMenuId(itemUid);

      const isSentralFeatureEnabled = checkIsSentralFeatureEnabledInCurriculum(
        SENTRAL_PERMISSION_CONFIGURATIONS.ATTENDANCE,
        curriculumConfigMap,
        curriculumProgramId
      );

      if (isSentralFeatureEnabled) return false;

      if (ACLStore.can(ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION)) {
        const permissions = getRnPPermissions({
          item,
          nodes,
        });
        return checkNodeVisibility({
          permissions,
          uid: item.uid,
          rootNode,
          isCurrentAcademicYearSelected,
          isFutureAcademicYearSelected,
        });
      }
      return ACLStore.can(item.perm);
    }
    case itemUid === "CIRCULARS": {
      if (ACLStore.can(ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION)) {
        return ACLStorage.getPermissionValue(
          "Announcements:CanCreateAndPublishAnnouncements"
        );
      }
      return false;
    }
    case itemUid === "BEHAVIOUR_MANAGEMENT": {
      const isSentralFeatureEnabled = checkIsSentralFeatureEnabledInCurriculums(
        SENTRAL_PERMISSION_CONFIGURATIONS.WELLBEING,
        curriculumConfigMap,
        curriculumProgramIds
      );

      if (isSentralFeatureEnabled) return false;

      const isBehaviourEnabledInRnp = ACLStore.can(
        ENABLE_RNP_CONSUMPTION_IN_BEHAVIOUR
      );

      if (
        !ACLStore.can(ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION) ||
        isBehaviourEnabledInRnp
      ) {
        return false;
      }

      const isBehaviourSetupCardEnabled = getIsBehaviourSetupCardEnabled();

      const hasAdminPortalAccess = ACLStorage.getModuleSettingsValueByContext(
        ADMIN_PORTAL_ACCESS_PERMISSION,
        []
      );

      return (
        ACLStore.can(item.perm) &&
        !isBehaviourSetupCardEnabled &&
        hasAdminPortalAccess
      );
    }
    case itemUid === "BEHAVIOUR_CONFIGURATION": {
      const isSentralFeatureEnabled = checkIsSentralFeatureEnabledInCurriculums(
        SENTRAL_PERMISSION_CONFIGURATIONS.WELLBEING,
        curriculumConfigMap,
        curriculumProgramIds
      );

      if (isSentralFeatureEnabled) return false;

      const isBehaviourSetupCardEnabled = getIsBehaviourSetupCardEnabled();

      if (!isBehaviourSetupCardEnabled) {
        return false;
      }

      const isBehaviourEnabledInRnp = ACLStore.can(
        [
          ENABLE_RNP_CONSUMPTION_IN_BEHAVIOUR,
          ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION,
        ],
        "all"
      );
      const hasModuleAdminAccess = getBehaviourAdminAccess();

      return isBehaviourEnabledInRnp
        ? checkNodeVisibility({
            permissions: getRnPPermissions({ item, nodes }),
            uid: item.uid,
            rootNode,
            isCurrentAcademicYearSelected,
            isFutureAcademicYearSelected,
          })
        : ACLStore.can(item.perm) && hasModuleAdminAccess;
    }
    case itemUid === "ACADEMY_ANALYTICS": {
      if (!ACLStore.can(ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION)) {
        return ACLStore.can(item.perm);
      }

      const hasAcademyModuleAccess = ACLStorage.getModuleSettingsValueByContext(
        item.permission,
        []
      );

      return hasAcademyModuleAccess && ACLStore.can(item.perm);
    }
    case itemUid === "CHECKLIST_V2": {
      const permissions = getRnPPermissions({
        item,
        nodes,
      });
      return (
        checkNodeVisibility({
          permissions,
          uid: item.uid,
          rootNode,
          isCurrentAcademicYearSelected,
          isFutureAcademicYearSelected,
        }) && ACLStore.can(item.perm)
      );
    }
    case itemUid === "TRASH_LEARNING_COURSES": {
      const isLearningCourseEnabled = getIsLearningCoursesEnabled({
        curriculumProgramId,
      });

      return isLearningCourseEnabled;
    }

    case /.+(DP_IBIS_EXAM_REGISTRATION)$/.test(itemUid): {
      const permissions = getRnPPermissions({
        item,
        nodes,
      });

      return (
        checkNodeVisibility({
          permissions,
          uid: item.uid,
          rootNode,
          isCurrentAcademicYearSelected,
          isFutureAcademicYearSelected,
        }) && !getIsIBISUIRevampEnabled()
      );
    }

    case /.+(DP_IBIS_REGISTRATION)$/.test(itemUid): {
      const permissions = getRnPPermissions({
        item,
        nodes,
      });

      return (
        checkNodeVisibility({
          permissions,
          uid: item.uid,
          rootNode,
          isCurrentAcademicYearSelected,
          isFutureAcademicYearSelected,
        }) && getIsIBISUIRevampEnabled()
      );
    }

    case itemUid === "ACTIVITY_LOGS_V2": {
      const isAdmin = getIsAdminByRole();
      if (
        !ACLStore.can(ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION) &&
        !isAdmin &&
        item.skipForTeacher
      ) {
        return false;
      } else {
        const permissions = getRnPPermissions({
          item,
          nodes,
        });

        return checkNodeVisibility({
          permissions,
          uid: item.uid,
          rootNode,
          isCurrentAcademicYearSelected,
          isFutureAcademicYearSelected,
        });
      }
    }

    case _.includes(itemUid, "PYP_EDUCATOR_CENTER_V2"):
    case _.includes(itemUid, "MYP_EDUCATOR_CENTER_V2"):
    case _.includes(itemUid, "DP_EDUCATOR_CENTER_V2"):
    case _.includes(itemUid, "UBD_EDUCATOR_CENTER_V2"): {
      const permissions = getRnPPermissions({
        item,
        nodes,
      });

      return checkNodeVisibility({
        permissions,
        uid: item.uid,
        rootNode,
        isCurrentAcademicYearSelected,
        isFutureAcademicYearSelected,
      });
    }

    case _.includes(itemUid, "TRANSCRIPTS"): {
      const permissions = getRnPPermissions({
        item,
        nodes,
      });

      return (
        checkNodeVisibility({
          permissions,
          uid: item.uid,
          rootNode,
          isCurrentAcademicYearSelected,
          isFutureAcademicYearSelected,
        }) && !ACLStore.can(IS_TRANSCRIPT_DISABLED_FOR_ORG)
      );
    }

    default: {
      const permissions = getRnPPermissions({
        item,
        nodes,
      });

      return checkNodeVisibility({
        permissions,
        uid: item.uid,
        rootNode,
        isCurrentAcademicYearSelected,
        isFutureAcademicYearSelected,
      });
    }
  }
};
